/**
 * CR313: Feature Flags Header
 * 
 * Sticky header with search, filters, and Save/Reset actions.
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, RotateCcw, Search, Loader2 } from 'lucide-react';
import { FEATURE_FLAG_CATEGORIES, type FeatureFlagCategory } from '@/lib/admin/featureFlagsRegistry';

export type StatusFilter = 'all' | 'default' | 'overridden';
export type TypeFilter = 'all' | 'boolean' | 'rollout' | 'json';

interface FeatureFlagsHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
  categoryFilter: FeatureFlagCategory | 'all';
  onCategoryFilterChange: (value: FeatureFlagCategory | 'all') => void;
  typeFilter: TypeFilter;
  onTypeFilterChange: (value: TypeFilter) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onReset: () => void;
}

export function FeatureFlagsHeader({
  search,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  isDirty,
  isSaving,
  onSave,
  onReset,
}: FeatureFlagsHeaderProps) {
  return (
    <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {/* Title */}
          <h1 className="text-xl font-semibold shrink-0">Feature Flags</h1>
          
          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search label or key..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-2">
            <Select value={categoryFilter} onValueChange={(v) => onCategoryFilterChange(v as FeatureFlagCategory | 'all')}>
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {FEATURE_FLAG_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={(v) => onTypeFilterChange(v as TypeFilter)}>
              <SelectTrigger className="w-[120px] h-9">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="boolean">Boolean</SelectItem>
                <SelectItem value="rollout">Rollout</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={(v) => onStatusFilterChange(v as StatusFilter)}>
              <SelectTrigger className="w-[130px] h-9">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="overridden">Overridden</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onReset}
            disabled={!isDirty || isSaving}
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Reset
          </Button>
          
          <Button
            size="sm"
            onClick={onSave}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
